import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Box, Flex } from 'theme-ui';

export type Tab = {
  id: string;
  label: string;
};

const SetTabActionsContext = createContext<(node: ReactNode) => void>(() => {});

/**
 * Lets a tab's own content render controls (e.g. pagination) into the shared tab-bar row,
 * right of the tab labels, instead of inside that tab's own content area. Clears itself on
 * unmount so switching tabs doesn't leave a stale action behind.
 */
export function useTabActions(node: ReactNode) {
  const setActions = useContext(SetTabActionsContext);
  useEffect(() => {
    setActions(node);
    return () => setActions(null);
  }, [node, setActions]);
}

export default function Tabs({
  tabs,
  children,
  activeTab,
  onTabChange,
}: {
  tabs: Tab[];
  activeTab: string;
  children: ReactNode;
  onTabChange: (tabId: string) => void;
}) {
  const [actions, setActions] = useState<ReactNode>(null);

  return (
    <Box>
      <Flex
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderBottomColor: 'secondaryHighlight',
        }}
      >
        <Flex sx={{ gap: 0, flex: 1 }}>
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <Box
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                sx={{
                  px: 4,
                  py: 3,
                  fontSize: 1,
                  cursor: 'pointer',
                  borderBottom: '2px solid',
                  transition: 'all 0.2s ease',
                  fontWeight: isActive ? 'bold' : 'normal',
                  color: isActive ? 'primary' : 'neutralDark',
                  borderBottomColor: isActive ? '#FFD102' : 'transparent',
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Flex>
        {actions}
      </Flex>
      <SetTabActionsContext.Provider value={setActions}>
        {children}
      </SetTabActionsContext.Provider>
    </Box>
  );
}
